import { useState } from 'react';
import { LayoutEditorFullscreenReference } from '../../../types/context';
import {
  FullscreenReferenceType,
  GridInputValue,
  LayoutCanvas,
  LayoutDefinition,
  LayoutObject,
} from '../../../types/types';
import { coerceLayoutFieldValue } from '../../../utils/layouts/coerceLayoutFieldValue';

type UseLayoutFullscreenReferenceParams = {
  definition: LayoutDefinition | undefined;
  selectedIndex: number | null;
  patchCanvas: (patch: Partial<LayoutCanvas>) => void;
  updateObject: (index: number, patch: Partial<LayoutObject>) => void;
  patchMatrixFormData: (name: string, value: GridInputValue) => void;
};

type UseLayoutFullscreenReference = {
  fullscreenReference: LayoutEditorFullscreenReference | null;
  openFullscreenReference: (
    reference: FullscreenReferenceType,
    scope: LayoutEditorFullscreenReference['scope']
  ) => void;
  closeFullscreenReference: () => void;
  submitFullscreenReference: (value: string) => void;
};

// Fullscreen reference picker of the layout editor: which field opened it and
// where the picked value goes back to (canvas / object / matrix dialog).
export const useLayoutFullscreenReference = ({
  definition,
  selectedIndex,
  patchCanvas,
  updateObject,
  patchMatrixFormData,
}: UseLayoutFullscreenReferenceParams): UseLayoutFullscreenReference => {
  const [fullscreenReference, setFullscreenReference] =
    useState<LayoutEditorFullscreenReference | null>(null);

  const openFullscreenReference = (
    reference: FullscreenReferenceType,
    scope: LayoutEditorFullscreenReference['scope']
  ): void => {
    setFullscreenReference({ reference, scope });
  };

  const closeFullscreenReference = (): void => {
    setFullscreenReference(null);
  };

  // Value picked in the fullscreen reference — written through the same paths
  // as the panel fields, with the numeric coercion by the target item type.
  const submitFullscreenReference = (value: string): void => {
    if (!fullscreenReference || !definition) {
      return;
    }

    const { reference, scope } = fullscreenReference;
    const targetForm =
      scope === 'canvas'
        ? definition.canvasForm
        : scope === 'matrix'
          ? definition.matrixForm
          : definition.objectForm;
    const itemType = targetForm?.items.find(
      (item) => item.name === reference.name
    )?.type;
    const coercedValue = coerceLayoutFieldValue(itemType, value);

    if (scope === 'matrix') {
      patchMatrixFormData(reference.name, coercedValue);
    } else if (scope === 'canvas') {
      patchCanvas({ [reference.name]: coercedValue });
    } else if (selectedIndex !== null) {
      updateObject(selectedIndex, { [reference.name]: coercedValue });
    }

    setFullscreenReference(null);
  };

  return {
    fullscreenReference,
    openFullscreenReference,
    closeFullscreenReference,
    submitFullscreenReference,
  };
};
